/**
 * `ChannelStep1Notice` — the Agent tab's honest notice for a channel that is
 * NOT the lawyer's yet. Two variants, one rule.
 *
 * - `"step1"` (S10 1b) — endpoint 1 is live, endpoint 2 is not: the channel is
 *   *live and ownerless*, never connected, ready or set up
 *   (`ai-docs/lexiun-user-setup.md` §2-bis). Names the missing step and offers
 *   the way into it.
 * - `"savedNotActive"` (RETEST-1 I-15, operator-decided 2026-09-11) — the token
 *   is STORED but the runtime is not running it (`/details` `requiresRoll`), so
 *   the bot is deaf. Without this the tab renders the nothing-set-up state and
 *   the lawyer believes the step was lost. It carries **no action button**:
 *   activation without a roll has not shipped
 *   (`ai-docs/telegram-activation-no-roll-miniplan.md`, option A), so a button
 *   would promise a path that cannot complete.
 *
 * Presentational only: the host owns the dialog and decides which variant
 * applies. Styled like the assistant card's "Etapa 1 de 2" chip — an amber dot,
 * never a success check, never green — so the two surfaces tell one story.
 */
import { useTranslation } from "react-i18next";

export function ChannelStep1Notice({
  variant = "step1",
  onContinue,
}: {
  variant?: "step1" | "savedNotActive";
  /** Required by the `step1` variant; `savedNotActive` offers no action. */
  onContinue?: () => void;
}) {
  const { t } = useTranslation();
  const savedNotActive = variant === "savedNotActive";

  return (
    <div
      data-testid={savedNotActive ? "channel-saved-not-active-notice" : "channel-step1-notice"}
      role="status"
      className="flex flex-wrap items-center gap-3 rounded-lg border border-amber-300/60 bg-amber-50 px-4 py-3 dark:border-amber-500/30 dark:bg-amber-500/10"
    >
      <span aria-hidden className="h-2 w-2 shrink-0 rounded-full bg-amber-500" />
      <p className="min-w-0 flex-1 text-body-sm text-amber-900 dark:text-amber-200">
        {savedNotActive ? (
          <>
            {t("agent.channels.savedNotActive")}{" "}
            <span className="text-amber-800/90 dark:text-amber-200/80">
              {t("agent.channels.savedNotActiveHint")}
            </span>
          </>
        ) : (
          t("agent.channels.step1Pending")
        )}
      </p>
      {!savedNotActive && (
        <button
          type="button"
          data-testid="channel-step1-continue"
          onClick={onContinue}
          className="shrink-0 rounded-md bg-gold-500 px-3 py-1.5 text-body-sm font-semibold text-coffee-900 transition-colors hover:bg-gold-400"
        >
          {t("agent.channels.step1PendingCta")}
        </button>
      )}
    </div>
  );
}
